import { parsePossiblyEmptyResponse } from "shared-services";
import { fetchRequest } from "../helpers/fetch-request";
import {
  IAuthParams,
  ICreateApplicationRequest,
  ICreateApplicationResponse,
  IForgotPasswordRequest,
  IForgotPasswordResponse,
  IGeneratePresignedUrlRequest,
  IGetApplicationResponse,
  IGetCreditProductionOptionsResponse,
  IGetMarketingPreferencesResponse,
  IGetPerformerTypesRequest,
  IGetQualificationLevelsResponse,
  ILoginApplicationRequest,
  ISaveAboutYouInformationRequest,
  ISaveCoursesRequest,
  ISaveCreditsRequest,
  ISaveMarketingPreferencesRequest,
  ISaveRecommendationRequest,
  IUpdateApplicationPathwayRequest,
  ICreateYPApplicationRequestBody,
  IAgentCodeUrlParam,
  ICreateCDApplicationRequestBody,
  ISaveAboutYouInformationCD,
  ICompleteApplicationData,
  ICreateAgentApplicationRequestBody,
} from "./user-account-service.types";
import { ICDApplication } from "./online-application.types";

export class UserAccountService {
  private url: string;

  constructor({ url }: { url: string }) {
    this.url = url;
  }

  createApplication(
    params: ICreateApplicationRequest
  ): Promise<ICreateApplicationResponse> {
    return fetchRequest(`${this.url}/application/create`, {
      method: "POST",
      body: params,
    }).then((res) => res.json());
  }

  createYPApplication(
    params: IAgentCodeUrlParam,
    body: ICreateYPApplicationRequestBody
  ): Promise<ICreateApplicationResponse> {
    return fetchRequest(
      `${this.url}/application/yp/create?${new URLSearchParams(params)}`,
      {
        method: "POST",
        body,
      }
    ).then((res) => res.json());
  }

  createCDApplication(
    body: ICreateCDApplicationRequestBody,
    isNewJoiningCDProcess?: boolean
  ): Promise<ICreateApplicationResponse> {
    const url = `${this.url}${
      isNewJoiningCDProcess ? "/casting/create" : "/cdapplication/create"
    }`;

    return fetchRequest(url, {
      method: "POST",
      body,
    }).then((res) => res.json());
  }

  getApplication(authParams: IAuthParams): Promise<IGetApplicationResponse> {
    return fetchRequest(`${this.url}/application`, { authParams }).then((res) =>
      res.json()
    );
  }

  loginToApplication(
    params: ILoginApplicationRequest
  ): Promise<IGetApplicationResponse> {
    return fetchRequest(`${this.url}/application/resume`, {
      method: "POST",
      body: params,
    }).then((res) => res.json());
  }

  validateYPAgent(params: IAgentCodeUrlParam) {
    return fetchRequest(
      `${this.url}/application/validateypagent?${new URLSearchParams(params)}`
    ).then((res) => res.json);
  }

  retrieveApplication(): Promise<IGetApplicationResponse> {
    return fetchRequest(`${this.url}/application`).then((res) => res.json());
  }

  getQualificationLevels(): Promise<IGetQualificationLevelsResponse> {
    return fetchRequest(`${this.url}/application/qualificationlevels`).then(
      (res) => res.json()
    );
  }

  generatePresignedUrl(
    params: IGeneratePresignedUrlRequest,
    authParams: IAuthParams,
    isNewJoiningCDProcess?: boolean
  ): Promise<string> {
    const authData = isNewJoiningCDProcess
      ? {
          id: authParams.onlineApplicationId,
          securityKey: authParams.securityKey,
        }
      : authParams;

    return fetchRequest(
      `${this.url}${
        isNewJoiningCDProcess
          ? "/casting/generatepresignedurl"
          : "/application/generatepresignedurl"
      }`,
      {
        method: "POST",
        body: params,
        authParams: authData,
        headers: {
          accept: "application/text",
        },
      }
    ).then((res) => res.text());
  }

  updateApplicationPathway(
    params: IUpdateApplicationPathwayRequest,
    authParams: IAuthParams
  ) {
    return fetchRequest(`${this.url}/application/pathway`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  saveCredits(params: ISaveCreditsRequest, authParams: IAuthParams) {
    return fetchRequest(`${this.url}/application/credits`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  saveCourses(params: ISaveCoursesRequest, authParams: IAuthParams) {
    return fetchRequest(`${this.url}/application/training`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  saveRecommendation(
    params: ISaveRecommendationRequest,
    authParams: IAuthParams
  ) {
    return fetchRequest(`${this.url}/application/recommendations`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  saveMarketingPreferences(
    params: ISaveMarketingPreferencesRequest,
    authParams: IAuthParams
  ): Promise<string> {
    return fetchRequest(`${this.url}/application/marketingpreferences`, {
      method: "POST",
      body: params,
      authParams,
    })
      .then((res) => res.json())
      .catch(() => ({}));
  }

  getMarketingPreferences(): Promise<IGetMarketingPreferencesResponse> {
    return fetchRequest(`${this.url}/application/marketingpreferences`).then(
      (res) => res.json()
    );
  }

  getPerformerTypes(): Promise<IGetPerformerTypesRequest> {
    return fetchRequest(`${this.url}/application/performertypes`).then((res) =>
      res.json()
    );
  }

  forgotPassword(
    params: IForgotPasswordRequest
  ): Promise<IForgotPasswordResponse> {
    return fetchRequest(`${this.url}/application/forgotpassword`, {
      method: "POST",
      body: params,
    }).then((res) => parsePossiblyEmptyResponse(res));
  }

  completeApplication(
    authParams: IAuthParams,
    body?: ICompleteApplicationData,
    overridePath?: string
  ) {
    const url = `${this.url}${overridePath ?? "/application/completed"}`;

    return fetchRequest(url, {
      method: "POST",
      body: body || {},
      authParams,
    });
  }

  saveAboutYouInformation(
    params: ISaveAboutYouInformationRequest,
    authParams: IAuthParams
  ) {
    return fetchRequest(`${this.url}/application/aboutyou`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  saveAboutYouInformationCD(
    params: ISaveAboutYouInformationCD,
    authParams: IAuthParams,
    isNewJoiningCDProcess?: boolean
  ): Promise<{ accountId: string }> {
    return fetchRequest(
      `${this.url}${
        isNewJoiningCDProcess ? "/casting/aboutyou" : "/cdapplication/aboutyou"
      }`,
      {
        method: "POST",
        body: params,
        authParams,
      }
    ).then((res) => res.json());
  }

  saveYPAboutYouInformation(
    params: ISaveAboutYouInformationRequest,
    authParams: IAuthParams
  ) {
    return fetchRequest(`${this.url}/application/yp/aboutyou`, {
      method: "POST",
      body: params,
      authParams,
    });
  }

  getCreditProductionOptions(): Promise<IGetCreditProductionOptionsResponse> {
    return fetchRequest(`${this.url}/application/productiontypes`).then((res) =>
      res.json()
    );
  }

  getLookup(groupId: number): Promise<{ label: string; value: number }[]> {
    return fetchRequest(`${this.url}/lookup?groupid=${groupId}`).then((res) =>
      res.json()
    );
  }

  getCDApplication(
    authParams: IAuthParams,
    isNewJoiningCDProcess?: boolean
  ): Promise<ICDApplication> {
    return fetchRequest(
      `${this.url}${isNewJoiningCDProcess ? "/casting" : "/cdapplication"}`,
      { authParams }
    ).then((res) => res.json());
  }

  saveMarketingPreferencesCD(
    params: ISaveMarketingPreferencesRequest,
    authParams: IAuthParams,
    isNewJoiningCDProcess?: boolean
  ): Promise<string> {
    return fetchRequest(
      `${this.url}${
        isNewJoiningCDProcess ? "/casting" : "/cdapplication"
      }/marketingpreferences`,
      {
        method: "POST",
        body: params,
        authParams,
      }
    ).then((res) => {
      // response from BE can be empty even when 200 ok response
      return res.text().then((text) => {
        try {
          const json = JSON.parse(text);

          return json;
        } catch (e: any) {
          console.error("Failed to parse marketing preferences: ", e);

          return "";
        }
      });
    });
  }

  completeCDApplication(
    authParams: IAuthParams,
    body: ICompleteApplicationData,
    isNewJoiningCDProcess?: boolean
  ) {
    return fetchRequest(
      `${this.url}${
        isNewJoiningCDProcess ? "/casting" : "/cdapplication"
      }/complete`,
      {
        method: "POST",
        body: body || {},
        authParams,
      }
    );
  }

  completeAgentApplication(params: ICreateAgentApplicationRequestBody) {
    return fetchRequest(`${this.url}/application/agent`, {
      method: "POST",
      body: params,
    });
  }
}
